// Saving a layout from a canvas that may hold fewer entries than the record (#1138).

type Pos = { x: number; y: number };

/**
 * The record to save: every position last loaded or saved, with the canvas over it, minus
 * anything deleted on the page. A layout PUT replaces the whole record, so a canvas without
 * the series entries must not erase their positions.
 */
export function mergeLayout(saved: Record<string, Pos>, canvas: Record<string, Pos>, deleted: Iterable<string>): Record<string, Pos> {
  const merged = { ...saved, ...canvas };
  for (const id of deleted) delete merged[id];
  return merged;
}

/** A save is safe only for the key whose layout was read in full; null means loading or a failed read. */
export function canSaveLayout(readyFor: string | null, key: string): boolean {
  return readyFor !== null && readyFor === key;
}

/** The server reads at most this many series ids in one request (`infra/lambda/platform/entities.js`). */
export const MAX_SERIES_PER_REQUEST = 10;

/**
 * Whether a load returns every entry that can have a position under its layout key, so
 * positions of anything else (deleted or untagged outside this view) can be dropped. A book
 * view holds only part of it with the switch off, with more series than the server reads, or
 * when the series list could not be read, since the book may be in a series after all.
 * The global and series views always hold their whole layout.
 */
export function viewHoldsWholeLayout(scope: string | null, seriesKnown: boolean, seriesSent: number, seriesOfBook: number): boolean {
  if (scope !== 'book') return true;
  return seriesKnown && seriesSent === seriesOfBook && seriesSent <= MAX_SERIES_PER_REQUEST;
}

/** Only the positions whose ids are in `present`. */
export function pruneLayout(saved: Record<string, Pos>, present: Set<string>): Record<string, Pos> {
  return Object.fromEntries(Object.entries(saved).filter(([id]) => present.has(id)));
}
